import { FieldProps } from 'components/Wizards/Wizard/Form/Types'
import { SelectInput } from 'components/Wizards/Wizard/Form/SelectInput'
import { Item } from '@react-stately/collections'
import { capitalize } from 'lodash'
import { SnippetsTextInput } from 'components/Wizards/Wizard/Form/SnippetsTextInput'
import { TextInput } from 'components/Wizards/Wizard/Form/TextInput'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { SelectPillsInput } from './SelectPillsInput'
import { SnippetTypeaheadInput } from './SnippetTypeaheadInput'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import Styles from './StringInput.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { BrandKitField } from 'components/Wizards/Wizard/Form/BrandKitField/BrandKitField'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Field as BaseField } from 'components/Wizards/Field/Field'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'

export const StringInput = ({ name, schema, autoFocus }: FieldProps) => {
  const isBrandKitsEnabled = useFlipperFlag('brand_kits')
  const isVirtualMarketer = useCurrentWorkspace()?.is_virtual_marketer
  const { isMobile } = useResponsiveDesign()
  const {
    meta: {
      placeholder,
      mobilePlaceholder,
      rows,
      snippets,
      typeahead,
      generationType,
      label,
      oneOfPills,
      pillsCarousel,
      withBrandKit,
      size,
    },
    oneOf,
  } = schema.describe()
  const isSelect = oneOf.length > 0
  const snippetKinds = typeof snippets !== 'boolean' ? snippets : undefined

  if (isSelect) {
    if (oneOfPills) {
      return (
        <SelectPillsInput name={name} aria-label={label} isCarousel={pillsCarousel} size={size}>
          {oneOf.map((option: string) => (
            <Item key={option}>{option}</Item>
          ))}
        </SelectPillsInput>
      )
    }

    return (
      <SelectInput name={name} aria-label={label}>
        {oneOf.map((option: string) => (
          <Item key={option}>{capitalize(option.toLowerCase())}</Item>
        ))}
      </SelectInput>
    )
  }

  if (snippets && typeahead && generationType) {
    return (
      <>
        <SnippetTypeaheadInput
          name={name}
          schema={schema}
          autoFocus={autoFocus}
          actions={
            isBrandKitsEnabled &&
            withBrandKit &&
            !isMobile && (
              <div className={Styles.brandKitField}>
                <Text variant="secondary" color="var(--dark-40)">
                  Brand Kit
                </Text>
                <BrandKitField size="xs" />
              </div>
            )
          }
        />
        {isBrandKitsEnabled && withBrandKit && isMobile && (
          <div className={Styles.mobileBrandKitField}>
            <BaseField
              name="brand_kit_id"
              label={isVirtualMarketer ? 'Your Brand Kit' : 'Select a Brand Kit'}
              direction="column"
            >
              <BrandKitField size="lg" />
            </BaseField>
          </div>
        )}
      </>
    )
  }

  if (snippets) {
    return (
      <>
        <ResponsiveDesign.Mobile>
          <SnippetsTextInput
            name={name}
            placeholder={mobilePlaceholder ?? placeholder}
            isTextarea={(rows || 1) > 1}
            rows={rows}
            kinds={snippetKinds}
            autoFocus={autoFocus}
            aria-label={name}
          />
        </ResponsiveDesign.Mobile>
        <ResponsiveDesign.Desktop>
          <SnippetsTextInput
            name={name}
            placeholder={placeholder}
            isTextarea={(rows || 1) > 1}
            rows={rows}
            kinds={snippetKinds}
            autoFocus={autoFocus}
            aria-label={name}
          />
        </ResponsiveDesign.Desktop>
      </>
    )
  }

  return <TextInput name={name} placeholder={placeholder} rows={rows} autoFocus={autoFocus} />
}
